import { ApiResponse, Culture, cultures, ListResult } from "../VolunteersHighlightContext/constants";

export const KVKK_LIST_PATH = "/kvkk";

/* -------------- Platforms and applications (PlatformEnum and AppEnum of the backend) -------------- */

// The backend also knows NoData (6); it is not a platform a text is written for, so it is not offered.
export const KVKK_PLATFORM_OPTIONS = [
	{ label: "IOS", value: 1 },
	{ label: "Android", value: 2 },
	{ label: "Web", value: 3 },
	{ label: "Huawei", value: 4 },
];

// The names the mobile version screen uses for the same applications.
export const KVKK_APP_OPTIONS = [
	{ label: "Moments", value: 1 },
	{ label: "IKCATI", value: 2 },
];

// The select components take and compare their values as text.
const toSelectOptions = (options: { label: string; value: number }[]) => options.map(option => ({ label: option.label, value: String(option.value) }));

export const KVKK_PLATFORM_SELECT_OPTIONS = toSelectOptions(KVKK_PLATFORM_OPTIONS);

export const KVKK_APP_SELECT_OPTIONS = toSelectOptions(KVKK_APP_OPTIONS);

const getOptionLabel = (options: { label: string; value: number }[], value: number) =>
	options.find(option => option.value === value)?.label ?? String(value);

export const getKvkkPlatformLabel = (platform: number) => getOptionLabel(KVKK_PLATFORM_OPTIONS, platform);

export const getKvkkAppLabel = (appId: number) => getOptionLabel(KVKK_APP_OPTIONS, appId);

/* -------------- LIST (response) -------------- */

// The title comes in the culture of the request, or in another culture when it is missing; older records have none.
export interface KvkkListItem {
	kvkkTextId: string;
	isManager: boolean;
	isPage: boolean;
	platform: number;
	appId: number;
	title: string | null;
	creationDate: string | null;
	updateDate: string | null;
}

export type KvkkListApiResponse = ApiResponse<ListResult<KvkkListItem>>;

/* -------------- GET (response) -------------- */

export interface KvkkLocalizable {
	kvkkTextLocalizableId: string;
	culture: string;
	title: string | null;
	// Rich text (HTML); older records were written outside the CMS editor and can hold lists, headings and styles.
	text: string | null;
}

export interface KvkkDetail {
	kvkkTextId: string;
	isManager: boolean;
	isPage: boolean;
	platform: number;
	appId: number;
	kvkkTextLocalizable: KvkkLocalizable[];
}

export type KvkkGetApiResponse = ApiResponse<KvkkDetail>;

/* -------------- ADD (form = request) -------------- */

export interface KvkkGeneralFormData {
	platform?: number;
	appId?: number;
	isPage: boolean;
	isManager: boolean;
}

export interface KvkkLocalizableFormData {
	culture: Culture;
	title: string;
	text: string;
}

export interface KvkkFormData extends KvkkGeneralFormData {
	kvkkTextLocalizable: KvkkLocalizableFormData[];
}

// A new text is most often the page text of a platform, which is what the KVKK pages and the project application show.
export const getDefaultKvkkFormData = (): KvkkFormData => ({
	platform: undefined,
	appId: undefined,
	isPage: true,
	isManager: false,
	kvkkTextLocalizable: cultures.map(culture => ({ culture, title: "", text: "" })),
});

/* -------------- EDIT (form state, complete) -------------- */

export interface KvkkEditLocalizable extends KvkkLocalizableFormData {
	// Empty for a culture that the record does not have yet.
	kvkkTextLocalizableId: string;
}

export interface KvkkEditFormData extends KvkkGeneralFormData {
	kvkkTextId: string;
	kvkkTextLocalizable: KvkkEditLocalizable[];
}

// Every culture gets a row so the form always shows both sections; a culture missing on the server keeps an empty id and
// is sent as a new localizable only when the user fills it in.
export const toKvkkEditFormData = (detail: KvkkDetail): KvkkEditFormData => ({
	kvkkTextId: detail.kvkkTextId,
	platform: detail.platform,
	appId: detail.appId,
	isPage: detail.isPage,
	isManager: detail.isManager,
	kvkkTextLocalizable: cultures.map(culture => {
		const localizable = detail.kvkkTextLocalizable.find(item => item.culture === culture);
		return {
			kvkkTextLocalizableId: localizable?.kvkkTextLocalizableId ?? "",
			culture,
			title: localizable?.title ?? "",
			text: localizable?.text ?? "",
		};
	}),
});

/* -------------- UPDATE (PUT request, partial - only changed fields) -------------- */

export interface UpdateKvkkLocalizable {
	// Left out for a culture that does not exist yet.
	kvkkTextLocalizableId?: string;
	culture: string;
	title?: string;
	text?: string;
}

// The id goes in the address, not in the body.
export interface UpdateKvkkRequest {
	platform?: number;
	appId?: number;
	isPage?: boolean;
	isManager?: boolean;
	kvkkTextLocalizable?: UpdateKvkkLocalizable[];
}

/* -------------- Rules (kept in sync with the backend validator) -------------- */

export const KvkkRules = {
	titleMaxLength: 250,
	titlePattern: /^[\p{L}\p{N}\s.,;:!?'"()\-_/&%+#@–—]*$/u,
	// The site's project application window falls back to the Turkish text when a culture has none, so a record needs it.
	requiredCulture: "tr-TR" as Culture,
} as const;
